import { Link } from "wouter";
import { useAuth } from "@/contexts/AuthContext";

interface AuthErrorPromptProps {
  /** Override heading. Defaults adapt to signed-in vs signed-out state. */
  title?: string;
  /** Override body copy. Defaults adapt to signed-in vs signed-out state. */
  message?: string;
}

/**
 * Renders a friendly sign-in nudge in place of protected content.
 *
 * Two modes, picked automatically from auth state:
 *   - Signed out → "Sign in to continue" + link to landing page
 *   - Signed in but auth error (token expired) → "Session expired" + sign-out
 */
export function AuthErrorPrompt({ title, message }: AuthErrorPromptProps = {}) {
  const { currentUser, signOut } = useAuth();
  const signedIn = !!currentUser;

  const heading = title ?? (signedIn ? "Session expired" : "Sign in to continue");
  const body =
    message ??
    (signedIn
      ? "Your session has expired. Sign in again to continue."
      : "Sign in to see your saved quotes, recommendations, and reading profile.");

  const buttonClass =
    "inline-block rounded-2xl bg-stone-900 px-6 py-2.5 text-sm font-medium text-white hover:bg-stone-700 transition";

  return (
    <div className="rounded-3xl border border-stone-200 bg-white p-12 text-center shadow-sm">
      <p className="text-xl font-semibold text-stone-900 mb-2">{heading}</p>
      <p className="text-sm text-stone-500 mb-6">{body}</p>
      {signedIn ? (
        <button onClick={signOut} className={buttonClass}>
          Sign in again
        </button>
      ) : (
        <Link href="/" className={buttonClass}>
          Sign in
        </Link>
      )}
    </div>
  );
}
